import { isProduction } from "@/lib/environment";
import { addDays, dayOfWeek, type IsoDate } from "@/lib/time/dates";

/** The jobs, when they run (UTC, as Vinyl's schedule) and the variable that turns each on. */

export const JOB_NAMES = [
  "late-flag",
  "signoff-stage",
  "late-notice",
  "te-weekly",
  "te-monthly",
  "pto-reminder",
  "pto-cram",
] as const;
export type JobName = (typeof JOB_NAMES)[number];

interface Schedule {
  hour?: number;
  minute?: number;
  /** Only on these days; every day when absent. */
  on?: (day: IsoDate) => boolean;
  /** Every hour on the hour, rather than once a day. */
  hourly?: boolean;
  /**
   * An hourly job's window in UTC: runs from `from`, last at the hour before `until`. An `until`
   * below `from` crosses midnight.
   */
  utcWindow?: { from: number; until: number };
  /** Past this age the slot is skipped: the email's deadline would already be gone. */
  maxAgeMs?: number;
}

const MONDAY = 1;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const MINUTE_MS = 60_000;

export const SCHEDULES: Record<JobName, Schedule> = {
  "late-flag": { hourly: true },
  "signoff-stage": { hour: 7, minute: 0 },
  "late-notice": { hour: 20, minute: 30 },
  "te-weekly": { hour: 10, minute: 0, on: (day) => dayOfWeek(day) === MONDAY, maxAgeMs: DAY_MS },
  "te-monthly": { hour: 10, minute: 0, on: (day) => day.endsWith("-01"), maxAgeMs: DAY_MS },
  "pto-reminder": { hour: 14, minute: 0 },
  // Vinyl's Hourly schedule, last event: 08:00 to 02:00 UTC, fixed, so it does not move with the
  // clocks. Late by more than this waits an hour, so a restart overnight can't write outside it.
  "pto-cram": { hourly: true, utcWindow: { from: 8, until: 2 }, maxAgeMs: 25 * MINUTE_MS },
};

const SWITCHES: Record<Exclude<JobName, "pto-cram">, string> = {
  "late-flag": "JOB_LATE_FLAG_ENABLED",
  "signoff-stage": "JOB_SIGNOFF_STAGE_ENABLED",
  "late-notice": "JOB_LATE_NOTICE_ENABLED",
  "te-weekly": "JOB_TE_WEEKLY_ENABLED",
  "te-monthly": "JOB_TE_MONTHLY_ENABLED",
  "pto-reminder": "JOB_PTO_REMINDER_ENABLED",
};

type Env = Record<string, string | undefined>;

/**
 * Off unless production: the seeded rows are real people's PTO, so the switch alone can't do it.
 * Live takes `isProduction` (the production host and the prod gateway) as well as
 * `PTO_CRAM_JOB=live`, set at the move from Vinyl (docs/operations.md). Dry run writes nothing and
 * is allowed anywhere. Any other value is off.
 */
export function ptoCramMode(env: Env): "off" | "dry-run" | "live" {
  if (env.PTO_CRAM_JOB === "dry-run") return "dry-run";
  return env.PTO_CRAM_JOB === "live" && isProduction(env) ? "live" : "off";
}

export const JOB_MODES = ["off", "dry-run", "live"] as const;
export type JobMode = (typeof JOB_MODES)[number];

/**
 * A job's mode, read each tick: the one place it is decided, so Settings, Jobs and the runner can
 * never disagree. The six notice jobs: off unless their switch is exactly `true`; dry run with
 * `JOBS_DRY_RUN=true`; live only on production (`isProduction`), and dry run anywhere else
 * (NOS2 rules `docs/app/jobs.md` rule 4: staging and local copies hold real people's data and
 * must never email them). pto-cram: `ptoCramMode`, which already needs `isProduction` for live;
 * `JOBS_DRY_RUN=true` outranks its live too.
 */
export function jobMode(job: JobName, env: Env): JobMode {
  const set = job === "pto-cram" ? ptoCramMode(env) !== "off" : env[SWITCHES[job]] === "true";
  if (!set) return "off";
  if (env.JOBS_DRY_RUN === "true" || (job === "pto-cram" && ptoCramMode(env) === "dry-run")) {
    return "dry-run";
  }
  return isProduction(env) ? "live" : "dry-run";
}

export const jobEnabled = (job: JobName, env: Env): boolean => jobMode(job, env) !== "off";
export const jobsDryRun = (job: JobName, env: Env): boolean => jobMode(job, env) === "dry-run";

/** What each job is called where a person reads it, and its schedule in words (UTC, saying so). */
export const JOB_LABELS: Record<JobName, { label: string; schedule: string }> = {
  "late-flag": { label: "Mark late sign-offs", schedule: "Every hour, on the hour (UTC)" },
  "signoff-stage": { label: "Create sign-off weeks and months", schedule: "Daily at 07:00 UTC" },
  "late-notice": { label: "Missing sign-off reminders", schedule: "Daily at 20:30 UTC" },
  "te-weekly": { label: "Weekly missing hours emails", schedule: "Mondays at 10:00 UTC" },
  "te-monthly": {
    label: "Monthly missing hours emails",
    schedule: "The 1st of each month at 10:00 UTC",
  },
  "pto-reminder": { label: "Upcoming PTO sign-off reminders", schedule: "Daily at 14:00 UTC" },
  "pto-cram": {
    label: "PTO allowances",
    schedule: "Every hour on the hour, 08:00 to 01:00 UTC",
  },
};

function inWindow({ utcWindow }: Schedule, at: Date): boolean {
  if (!utcWindow) return true;
  const hour = at.getUTCHours();
  if (utcWindow.from < utcWindow.until) return hour >= utcWindow.from && hour < utcWindow.until;
  return hour >= utcWindow.from || hour < utcWindow.until;
}

/** Far enough back to find the 1st of a month. */
const SEARCH_DAYS = 32;

/**
 * The latest slot at or before `now`. Earlier missed slots are never looked at, so after downtime
 * only this one can run.
 */
export function latestSlot(schedule: Schedule, now: Date): Date {
  if (schedule.hourly) {
    const hour = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
    );
    for (let back = 0; back < 24; back++) {
      const at = new Date(hour - back * HOUR_MS);
      if (inWindow(schedule, at)) return at;
    }
    throw new Error("No slot in range");
  }
  const today = now.toISOString().slice(0, 10);
  for (let back = 0; back <= SEARCH_DAYS; back++) {
    const day = addDays(today, -back);
    const at = new Date(
      `${day}T${String(schedule.hour ?? 0).padStart(2, "0")}:${String(schedule.minute ?? 0).padStart(2, "0")}:00Z`,
    );
    if (at <= now && (!schedule.on || schedule.on(day))) return at;
  }
  throw new Error("No slot in range");
}

/** The first slot after `now`: when the job runs next. */
export function nextSlot(schedule: Schedule, now: Date): Date {
  if (schedule.hourly) {
    const hour = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
    );
    for (let ahead = 1; ahead <= 24; ahead++) {
      const at = new Date(hour + ahead * HOUR_MS);
      if (inWindow(schedule, at)) return at;
    }
    throw new Error("No slot in range");
  }
  const today = now.toISOString().slice(0, 10);
  for (let ahead = 0; ahead <= SEARCH_DAYS; ahead++) {
    const day = addDays(today, ahead);
    const at = new Date(
      `${day}T${String(schedule.hour ?? 0).padStart(2, "0")}:${String(schedule.minute ?? 0).padStart(2, "0")}:00Z`,
    );
    if (at > now && (!schedule.on || schedule.on(day))) return at;
  }
  throw new Error("No slot in range");
}

export const isStale = (schedule: Schedule, slot: Date, now: Date): boolean =>
  schedule.maxAgeMs !== undefined && now.getTime() - slot.getTime() > schedule.maxAgeMs;

export const slotDay = (slot: Date): IsoDate => slot.toISOString().slice(0, 10);
